<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useRoute, useRouter } from 'vue-router';
import { fetchWrapper } from '@/utils/helpers/fetch-wrapper';
import { useNotificationStore } from '@/stores/notification';
import { mdiCheckCircle, mdiAlertCircle } from '@mdi/js';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const notificationStore = useNotificationStore();

const verificationStatus = ref<'pending' | 'success' | 'error'>('pending');
const errorMessage = ref<string | null>(null);
const loading = ref(false);

onMounted(async () => {
  const token = route.query.token as string;
  
  if (token) {
    try {
      loading.value = true;
      // Direct API call since verifyEmail is not in the store
      const response = await fetchWrapper.post(`${import.meta.env.VITE_API_URL}/auth/verify-email/`, { token });
      
      if (response && response.status === 'success') {
        verificationStatus.value = 'success';
        // Redirect to login after 3 seconds
        setTimeout(() => {
          router.push('/auth/login');
        }, 3000);
      } else {
        throw new Error(response?.message || 'Email verification failed');
      }
    } catch (error) {
      verificationStatus.value = 'error';
      errorMessage.value = error instanceof Error ? error.message : 'Email verification failed';
    } finally {
      loading.value = false;
    }
  }
});

const handleResendVerification = async () => {
  try {
    loading.value = true;
    // Direct API call since resendVerificationEmail is not in the store
    const response = await fetchWrapper.post(`${import.meta.env.VITE_API_URL}/auth/resend-verification/`, {});
    
    if (response && response.status === 'success') {
      verificationStatus.value = 'success';
      notificationStore.showSuccess({
        title: 'Email Sent',
        text: 'Verification email has been resent. Please check your inbox.'
      });
    } else {
      throw new Error(response?.message || 'Failed to resend verification email');
    }
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Failed to resend verification email';
    notificationStore.showError({
      title: 'Error',
      text: errorMessage.value
    });
  } finally {
    loading.value = false;
  }
};
</script>

<template>
  <div class="text-center">
    <template v-if="verificationStatus === 'pending'">
      <h5 class="text-h5 my-4 mb-8">Verifying your email...</h5>
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      />
    </template>

    <template v-else-if="verificationStatus === 'success'">
      <v-icon
        color="success"
        :icon="mdiCheckCircle"
        size="64"
        class="my-4"
      />
      <h5 class="text-h5 my-4 mb-8">Email verified successfully!</h5>
      <p class="text-body-1 mb-4">You will be redirected to the login page shortly.</p>
    </template>

    <template v-else>
      <v-icon
        color="error"
        :icon="mdiAlertCircle"
        size="64"
        class="my-4"
      />
      <h5 class="text-h5 my-4 mb-8">Email verification failed</h5>
      <p class="text-body-1 mb-4">{{ errorMessage }}</p>
      
      <v-btn
        color="primary"
        variant="flat"
        size="large"
        class="mt-4"
        @click="handleResendVerification"
        :loading="loading"
        :disabled="loading"
      >
        Resend verification email
      </v-btn>
    </template>

    <v-alert
      v-if="authStore.error"
      color="error"
      class="mt-4"
      variant="tonal"
    >
      {{ authStore.error }}
    </v-alert>
  </div>
</template> 